import React from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Send, X } from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";

export default function CounselingLogCreationModal({
  isCreatingLog,
  isMobile,
  getLogIcon,
  inputType,
  getTypeColor,
  creationModalPos,
  onTouchStart,
  onTouchMove,
  onVerticalSwipeEnd,
  setIsCreatingLog,
  handleMouseDown,
  setInputType,
  theme,
  inputDate,
  setInputDate,
  inputSentiment,
  setInputSentiment,
  getSentimentIcon,
  inputContent,
  setInputContent,
  handleQuickSubmit,
  editingLogId,
}) {
  if (!isCreatingLog) return null;

  const iconSrc = getLogIcon(inputType);
  const colors = getTypeColor(inputType);

  return createPortal(
    <div className={`fixed inset-0 flex z-[10000] pointer-events-none ${isMobile ? "items-end" : "items-center justify-center p-4"}`}>
      <div
        className={`bg-white shadow-2xl relative border-4 ${colors.border} pointer-events-auto ${isMobile ? "w-full rounded-t-[2.5rem] rounded-b-none pb-safe" : "w-full max-w-sm rounded-[2.5rem]"}`}
        style={
          isMobile ? { maxHeight: "90vh", overflowY: "auto" } : { transform: `translate(${creationModalPos.x}px, ${creationModalPos.y}px)` }
        }
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={() => onVerticalSwipeEnd(() => setIsCreatingLog(false))}
      >
        {isMobile && (
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300"></div>
          </div>
        )}

        <div
          onMouseDown={!isMobile ? handleMouseDown : undefined}
          className={`bg-gradient-to-br from-white to-${colors.bg.replace("bg-", "")} ${isMobile ? "p-6 pt-2 pb-8" : "p-6 pb-8"} relative rounded-t-[2.2rem] ${!isMobile ? "cursor-move" : ""}`}
        >
          <button onClick={() => setIsCreatingLog(false)} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 bg-white/50 hover:bg-white p-2 rounded-full transition backdrop-blur-md z-50">
            <X size={20} />
          </button>
          <div className="flex flex-col items-center relative z-10">
            <div className={`w-20 h-20 rounded-[1.5rem] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.08)] flex items-center justify-center mb-4 ${colors.text} border-4 border-white`}>
              <img src={iconSrc} alt={inputType} className="w-10 h-10 object-contain drop-shadow-sm" />
            </div>

            <div className="flex flex-col items-center gap-2 w-full">
              <div className="flex gap-1 justify-center w-full overflow-x-auto pb-1 no-scrollbar">
                {["면담", "전화", "문자", "기도", "교제"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setInputType(t)}
                    className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-all ${inputType === t ? `${colors.bg} ${colors.text} shadow-md scale-105` : "bg-white/50 text-gray-500 hover:bg-white"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="w-[140px] relative z-50">
                <CustomDatePicker
                  theme={theme}
                  value={inputDate}
                  onChange={setInputDate}
                  className="w-full shadow-sm bg-white/60 text-xs font-bold border border-white/60 rounded-2xl text-center py-2 flex justify-center items-center"
                />
              </div>
            </div>
          </div>

          <div className="absolute top-0 left-0 w-full h-full opacity-10 overflow-hidden pointer-events-none rounded-t-[2.2rem]">
            <div className={`absolute -top-10 -left-10 w-40 h-40 rounded-full ${colors.bg} blur-3xl`}></div>
            <div className={`absolute top-10 right-0 w-32 h-32 rounded-full ${colors.bg} blur-2xl`}></div>
          </div>
        </div>

        <div className="p-8 pt-6 relative -mt-4 bg-white rounded-t-[2rem] rounded-b-[2.2rem]">
          <div className="flex flex-col items-center -mt-10 mb-10 relative z-10">
            <div className="bg-white p-1.5 rounded-2xl shadow-sm ring-1 ring-gray-100 flex gap-1 mb-2">
              {["happy", "neutral", "sad"].map((s) => (
                <button
                  key={s}
                  onClick={() => setInputSentiment(s)}
                  className={`p-2 rounded-xl transition-all duration-300 ${inputSentiment === s ? "bg-gray-100 scale-110 shadow-inner" : "opacity-40 hover:opacity-100"}`}
                >
                  {getSentimentIcon(s)}
                </button>
              ))}
            </div>
            <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2.5 py-1 rounded-lg">
              {inputSentiment === "happy" ? "기쁨 😊" : inputSentiment === "sad" ? "슬픔 😢" : "평온 😐"}
            </span>
          </div>

          <div className={`relative bg-gray-50/50 rounded-2xl p-6 min-h-[140px] max-h-[300px] overflow-hidden mb-6 border ${colors.border} shadow-inner group`}>
            <div className="absolute -bottom-2 -right-2 opacity-[0.1] transform rotate-[-15deg] group-hover:scale-110 group-hover:opacity-20 transition-all duration-500">
              <img src={iconSrc} alt="" className="w-32 h-32 object-contain grayscale" />
            </div>

            <div className="relative z-10 overflow-y-auto custom-scrollbar max-h-[260px] pr-2">
              <textarea
                value={inputContent}
                onChange={(e) => setInputContent(e.target.value)}
                className="w-full h-[140px] bg-transparent border-none outline-none resize-none text-gray-700 font-medium leading-relaxed text-[15px]"
                placeholder="오늘 나눈 대화, 기도제목, 특이사항을&#13;&#10;자유롭게 기록해보세요... ✍️"
              />
            </div>
          </div>

          <button onClick={handleQuickSubmit} className={`w-full py-3.5 rounded-2xl font-bold transition flex items-center justify-center gap-2 border shadow-sm ${colors.bg} ${colors.text} ${colors.border} hover:brightness-95`}>
            {editingLogId ? <CheckCircle2 size={18} /> : <Send size={18} />}
            {editingLogId ? "수정 완료" : "기록 저장하기"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
